import clsx from 'clsx'

import { ColumnSettings, ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColorVariant, MaterialIcon, MaterialIconProps, Rating } from '@makyoapp/makyo-tw-ui'

import { ICell } from '@/core/models/columns'
import { parseNumberValue } from '@/core/models/columns/number'
import { IRatingCell } from '@/core/models/columns/rating'
import { MarkdownPreview } from '@/lib/markdown'
import { THUMB_SRC } from '@/shared/constants'
import { createSlug } from '@/shared/utils'

const getFileNameFromUrl = (url: string): string => {
  if (!url) return ''
  const segments = url.split('/')
  return segments[segments.length - 1]
}

interface CellComponentProps {
  settings?: ColumnSettings
  cell?: ICell
  type: ColumnTypes
  fallbackImage?: boolean
  classNameImage?: string
  [key: string]: any
  backgroundColor?: ColorVariant
  iconProps?: Omit<MaterialIconProps, 'icon'>
}

export function CellComponent({
  settings,
  cell,
  type,
  fallbackImage,
  classNameImage,
  style,
  id,
  backgroundColor,
  iconProps,
  ...props
}: CellComponentProps) {
  if (!cell) return null
  const cellStr = cell.toString()

  switch (type) {
    case ColumnTypes.Image: {
      return fallbackImage && !cellStr ? (
        <img className={clsx('shrink-0 object-cover', classNameImage)} src={THUMB_SRC} {...props} />
      ) : (
        <img className={clsx('shrink-0 object-cover', classNameImage)} src={cellStr} {...props} />
      )
    }

    case ColumnTypes.Icon: {
      return cellStr ? <MaterialIcon icon={cellStr} {...props} {...iconProps} /> : null
    }

    case ColumnTypes.Rating: {
      const ratingCell = cell as IRatingCell
      return (
        <Rating
          backgroundColor={backgroundColor}
          readOnly
          count={settings?.ratingMax as number}
          value={ratingCell.value ? Number(ratingCell.value) : Number(ratingCell)}
          alignment={props.alignment}
        />
      )
    }

    case ColumnTypes.URL:
    case ColumnTypes.Attachment: {
      const maxLength = ColumnTypes.Attachment ? 30 : 50
      const label = ColumnTypes.Attachment ? getFileNameFromUrl(cellStr) : cellStr

      return cellStr?.startsWith('http') ? (
        <a href={cellStr} target='_blank' rel='noreferrer'>
          {label?.length > maxLength ? `${label.substring(0, maxLength)}...` : label}
        </a>
      ) : null
    }

    case ColumnTypes.Slug:
      return <>{createSlug(cellStr)}</>

    default:
      return <>{cellStr}</>
  }
}
